import { useEffect, useState } from 'react';
import { useCambiosSinGuardar, usePaso } from './ui/navegacionReact';
import { ArrowLeft, ArrowRight, Loader2, ShieldCheck } from 'lucide-react';
import CampoContrasena from './ui/CampoContrasena';
import { useAuth } from './lib/contextoAuth';
import {
  MarcoPuerta, PanelVisual, TexturaPuntos, CampoPuerta, BotonPuerta,
  AvisoError, Plegado,
} from './publico/puerta';
import { CLASE_CAMPO } from './publico/medidas';

/* =====================================================================
   LA PUERTA DE LOS CLIENTES · darse de alta
   =====================================================================
   Hermana de la de entrar: mismo esqueleto de dos paneles, mismas
   medidas, mismo botón. Su carácter es el de un paso más adentro — el
   mismo azul pero con el rescoldo arriba, y el panel cuenta lo que se
   lleva quien se da de alta en vez de una frase de marca sola.

   ERA UN FORMULARIO DE SIETE CAMPOS DE UN TIRÓN, y encima regañaba antes
   de tiempo: bastaba con salir del primer campo —que tiene el foco al
   abrir— para llevarse un «Escribe el nombre de la empresa» en rojo sin
   haber escrito nada. Ahora son TRES PASOS CORTOS con el avance a la
   vista, se pide lo mínimo primero, lo fiscal va al final y plegado, y
   NINGÚN error se escribe hasta que se pulsa el botón de ese paso.

   VOLVER ATRÁS NO PIERDE NADA: lo escrito vive en el estado del padre,
   no en cada paso.

   LO QUE NO SE TOCA: `POST /api/registro` y sus validaciones. Las reglas
   de aquí son las MISMAS que aplica el servidor, dichas antes de enviar
   para no descubrir a la séptima casilla que la contraseña era corta.

   EL PASO DE LA CONTRASEÑA CAMBIÓ EL 19 DE SEPTIEMBRE DE 2026, con la
   primera clienta real. Se escribía a ciegas y una sola vez, y esa es la
   única forma de entrar: una letra cambiada en el teléfono y la cuenta
   nacía con una contraseña que nadie conocía. Ahora hay un ojo para ver
   lo escrito, un segundo campo «Confirma tu contraseña», y el botón de
   crear queda apagado HASTA QUE COINCIDAN — diciendo debajo, en cada
   momento, qué falta. Es la excepción a «el botón no arranca apagado»
   de las puertas, y es deliberada: aquí lo que se está evitando es
   perder la cuenta antes de estrenarla.
   ===================================================================== */
const RFC = /^[A-ZÑ&]{3,4}[0-9]{6}[A-Z0-9]{3}$/i;

const REGLAS = {
  razon_social: (v) => (v.trim().length < 2 ? 'Escribe el nombre de la empresa.' : null),
  rfc: (v) => (v.trim() && !RFC.test(v.trim()) ? 'El RFC no tiene el formato esperado.' : null),
  telefono: (v) => (/^\+?\d{8,15}$/.test(v.replace(/[\s()-]/g, ''))
    ? null : 'El teléfono va de 8 a 15 dígitos.'),
  nombre_dueno: (v) => (v.trim().length < 3 ? 'Escribe tu nombre completo.' : null),
  email: (v) => (/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v.trim())
    ? null : 'Ese correo no parece completo. Revisa que lleve @ y dominio.'),
  password: (v) => {
    if (v.length < 12) return 'Al menos 12 caracteres.';
    if (/^\d+$/.test(v) || /^[a-zA-Z]+$/.test(v)) return 'Combina letras con números o símbolos.';
    return null;
  },
  // LA CONFIRMACIÓN SE JUZGA CONTRA LA OTRA. Recibe el formulario entero
  // porque necesita las dos; las demás reglas ignoran el segundo argumento.
  password2: (v, f) => (v !== f.password ? 'Las dos contraseñas no coinciden.' : null),
};

// POR QUÉ ESTÁ APAGADO EL BOTÓN DE CREAR, dicho en una frase y en orden:
// primero lo que falta de la contraseña, luego lo que falta de la
// confirmación. Vacío = ya se puede.
function motivoParaNoCrear(f) {
  if (!f.password) return 'Escribe una contraseña de al menos 12 caracteres, con números o símbolos.';
  const dice = REGLAS.password(f.password);
  if (dice) return `La contraseña todavía no sirve: ${dice.charAt(0).toLowerCase()}${dice.slice(1)}`;
  if (!f.password2) return 'Ahora escríbela otra vez en «Confirma tu contraseña».';
  if (f.password2 !== f.password) return 'Las dos contraseñas no coinciden todavía. Usa el ojo para compararlas.';
  return '';
}

// Qué se pide en cada paso. Lo mínimo primero: nombre y teléfono bastan
// para tener una empresa; el RFC es fiscal y va al final, plegado.
const PASOS = [
  { titulo: 'Tu empresa', campos: ['razon_social', 'telefono'] },
  { titulo: 'Tu cuenta de dueño', campos: ['nombre_dueno', 'email'] },
  { titulo: 'Tu contraseña', campos: ['password', 'password2', 'rfc'] },
];

export default function RegistroEmpresa() {
  const { registrarEmpresa } = useAuth();
  const [paso, setPaso] = useState(0);
  usePaso('alta-empresa', paso, setPaso);          // atrás = el paso anterior, con lo escrito intacto
  const [intentado, setIntentado] = useState([false, false, false]);
  const [f, setF] = useState({
    razon_social: '', rfc: '', telefono: '', sitio_web: '',
    nombre_dueno: '', email: '', password: '', password2: '',
  });
  // Un solo ojo para los dos campos: se comparan a la vista, que es para
  // lo que sirve.
  const [verClave, setVerClave] = useState(false);
  const [error, setError] = useState(null);
  // LO QUE EL SERVIDOR RECHAZÓ, CAMPO POR CAMPO (19 sep 2026). El servidor
  // vuelve a comprobar todo y contesta con el campo y el paso: hasta hoy
  // eso se tiraba y la persona veía «No se pudo completar lo que pediste»
  // en el paso 3 sin saber que el problema estaba en el paso 1 (un
  // teléfono que ya tiene cuenta). Ahora se salta a ese paso, se marca el
  // campo y se dice la frase del servidor debajo de él. Se borra en cuanto
  // se edita ese campo.
  const [delServidor, setDelServidor] = useState({});
  // EL FOCO VA AL CAMPO RECHAZADO DESPUÉS DE QUE SU PASO SE PINTE (19 sep
  // 2026, medido en producción): un `setTimeout(…, 0)` corría antes de que
  // React montara el paso nuevo, así que el foco acababa en el primer
  // campo del paso (su `autoFocus`) y no en el que el servidor señaló.
  // Un objeto nuevo por rechazo, para que el efecto corra aunque sea el
  // mismo campo dos veces.
  const [enfocar, setEnfocar] = useState(null);
  useEffect(() => {
    if (enfocar) document.getElementById(enfocar.campo)?.focus();
  }, [enfocar, paso]);
  const [enviando, setEnviando] = useState(false);
  // ALTA A MEDIAS · TC-DR-AT. Con algún campo escrito y el alta sin enviar,
  // atrás pregunta antes de volver al acceso. Entre pasos no pregunta: lo
  // escrito se conserva. (El campo trampa `sitio_web` no cuenta.)
  useCambiosSinGuardar(
    () => !enviando && Object.entries(f).some(([k, v]) => k !== 'sitio_web' && String(v).trim() !== ''),
    { sub: 'registro' },
  );
  const set = (k) => (e) => {
    const v = e.target.value;
    setF((s) => ({ ...s, [k]: v }));
    if (delServidor[k]) setDelServidor((d) => { const { [k]: _q, ...resto } = d; return resto; });
  };

  const ultimo = paso === PASOS.length - 1;
  const faltaEnEstePaso = PASOS[paso].campos
    .map((k) => ({ campo: k, dice: REGLAS[k](f[k], f) }))
    .filter((x) => x.dice);
  // EN EL ÚLTIMO PASO EL BOTÓN SE APAGA HASTA QUE LAS DOS COINCIDAN, y
  // dice por qué. En los otros dos rige la regla de las puertas: se
  // enciende y solo se apaga tras un intento con algo mal.
  const motivo = ultimo ? motivoParaNoCrear(f) : '';
  // El error de un campo solo se enseña desde el primer intento DE SU
  // PASO. Volver atrás no destapa los avisos de un paso que no se ha
  // intentado todavía.
  const errorDe = (k) => delServidor[k] || (intentado[paso] ? REGLAS[k](f[k], f) : null);

  function marcarIntento() {
    setIntentado((s) => s.map((v, i) => (i === paso ? true : v)));
  }

  async function enviar(e) {
    e.preventDefault();
    if (enviando) return;
    if (faltaEnEstePaso.length) {
      marcarIntento();
      document.getElementById(faltaEnEstePaso[0].campo)?.focus();
      return;
    }
    if (!ultimo) { setPaso(paso + 1); return; }
    if (motivo) return;

    setError(null);
    setEnviando(true);
    try {
      await registrarEmpresa({
        razon_social: f.razon_social.trim(),
        rfc: f.rfc.trim() || null,
        telefono: f.telefono.replace(/[\s()-]/g, ''),
        email: f.email.trim(),
        nombre_dueno: f.nombre_dueno.trim(),
        password: f.password,
        // El campo trampa viaja siempre. Mandarlo solo cuando tiene algo
        // dentro haria que su AUSENCIA delatara el mecanismo a quien
        // compare dos peticiones.
        sitio_web: f.sitio_web,
      });
      // No se navega a ninguna parte: al quedar la sesión abierta, App
      // cambia solo a la consola.
    } catch (err) {
      setEnviando(false);
      // Si el servidor dijo QUÉ campo, se va a su paso y se marca ahí; el
      // texto general no se repite encima.
      const campo = err && err.campo && REGLAS[err.campo] ? err.campo : null;
      if (campo) {
        const i = PASOS.findIndex((pp) => pp.campos.includes(campo));
        setDelServidor((d) => ({ ...d, [campo]: err.message }));
        setError(null);
        if (i >= 0) { setPaso(i); setIntentado((s) => s.map((v, j) => (j === i ? true : v))); }
        setEnfocar({ campo });
        return;
      }
      setError(err.message || 'No se pudo completar el alta.');
    }
  }

  return (
    <MarcoPuerta panel={<PanelDeAlta />}>
      {/* EN EL PRIMER PASO SE SALE AL SITIO; en los demás se retrocede.
          Son dos cosas distintas y por eso son dos elementos: salir es
          un enlace —se puede copiar y abrir aparte— y retroceder es un
          botón, porque no cambia de dirección.

          Antes las dos eran un `<button>` que llamaba a
          `onNavigate('LANDING')`, y esa portada ya no se pinta: quien
          pulsaba se quedaba en el mismo sitio sin que pasara nada. */}
      {paso === 0 ? (
        // M4 · `min-h-11` da los 44 px de alto de un blanco táctil sin
        // agrandar la letra; ya era `inline-flex items-center`.
        <a
          href="/"
          className="inline-flex items-center gap-1.5 min-h-11 text-sm font-semibold text-[var(--p-tinta-2)] hover:text-[var(--p-tinta)] mb-8 transition-colors duration-150"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          Volver al sitio
        </a>
      ) : (
        <button
          type="button"
          onClick={() => setPaso(paso - 1)}
          className="inline-flex items-center gap-1.5 min-h-11 text-sm font-semibold text-[var(--p-tinta-2)] hover:text-[var(--p-tinta)] mb-8 transition-colors duration-150"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          Paso anterior
        </button>
      )}

      {/* M4 · el wordmark de teléfono es un enlace a la portada: 44 px de
          alto para el pulgar, con la misma letra. */}
      <a href="/" className="lg:hidden inline-flex items-center min-h-11 text-xl font-bold tracking-tight mb-6">
        TEGO<span className="text-[var(--p-marca)]">Cloud</span>
      </a>

      <Avance paso={paso} />

      <h1 className="mt-5 text-[1.75rem] leading-tight font-semibold tracking-tight">
        {PASOS[paso].titulo}
      </h1>
      {/* EL SUBTÍTULO ES CUERPO: la frase que explica el paso se lee
          entera, así que va a 16 px (`text-base`, era 0.9375rem = 15) para
          cumplir M5 (cuerpo ≥ 16 px). */}
      <p className="mt-2 text-base leading-relaxed text-[var(--p-tinta-2)]">
        {[
          'Con dos datos basta para empezar. Lo demás se puede poner después.',
          'Esta cuenta nace con el control total, y nadie te lo puede quitar desde dentro.',
          'Es la única forma de entrar: aquí no hay correo de recuperación.',
        ][paso]}
      </p>

      <form onSubmit={enviar} className="mt-8 space-y-5" noValidate>
        {/* CAMPO TRAMPA (deuda V-7). Un robot de alta rellena todo lo que
            encuentra; una persona no puede tocar esto ni queriendo:
            `aria-hidden` lo saca del árbol de accesibilidad —la parte que
            se olvida, y la que convierte la trampa en una barrera para
            quien no ve—, `tabIndex={-1}` del tabulador, y se esconde
            fuera de pantalla y no con `display:none`, que los robots que
            miran sí saltan. El nombre `sitio_web` es deliberado:
            `honeypot` sería un cartel diciendo dónde está la trampa.

            VA FUERA DE LOS PASOS, siempre montado: si solo existiera en
            el último, un robot que envía el primero no lo vería. */}
        <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', top: 0 }}>
          <label htmlFor="sitio_web">Sitio web</label>
          <input id="sitio_web" name="sitio_web" type="text" tabIndex={-1}
                 autoComplete="off" value={f.sitio_web} onChange={set('sitio_web')} />
        </div>

        {paso === 0 && (
          <>
            <CampoPuerta id="razon_social" etiqueta="Nombre de la empresa"
                         error={errorDe('razon_social')}
                         ayuda="El que verán tus empleados al entrar.">
              <input id="razon_social" value={f.razon_social} onChange={set('razon_social')}
                     autoFocus placeholder="Bodegas del Norte" className={CLASE_CAMPO} />
            </CampoPuerta>
            <CampoPuerta id="telefono" etiqueta="Teléfono" error={errorDe('telefono')}
                         ayuda="Por si hay que recuperar el acceso.">
              <input id="telefono" type="tel" inputMode="tel" value={f.telefono}
                     onChange={set('telefono')} placeholder="8112345678" className={CLASE_CAMPO} />
            </CampoPuerta>
          </>
        )}

        {paso === 1 && (
          <>
            <CampoPuerta id="nombre_dueno" etiqueta="Tu nombre completo"
                         error={errorDe('nombre_dueno')}>
              <input id="nombre_dueno" value={f.nombre_dueno} onChange={set('nombre_dueno')}
                     autoFocus placeholder="María Fernanda Ruiz" className={CLASE_CAMPO} />
            </CampoPuerta>
            <CampoPuerta id="email" etiqueta="Correo electrónico" error={errorDe('email')}
                         ayuda="Con este entrarás a partir de ahora.">
              <input id="email" type="email" inputMode="email" autoComplete="username"
                     value={f.email} onChange={set('email')}
                     placeholder="maria@empresa.com" className={CLASE_CAMPO} />
            </CampoPuerta>
          </>
        )}

        {paso === 2 && (
          <>
            <CampoPuerta id="password" etiqueta="Contraseña" error={errorDe('password')}
                         ayuda="Mínimo 12 caracteres, con números o símbolos.">
              <CampoContrasena id="password"
                               autoComplete="new-password" autoFocus
                               value={f.password} onChange={set('password')}
                               placeholder="••••••••••••" className={`${CLASE_CAMPO} pr-24`}
                               claseBoton="text-[var(--p-tinta-2)] hover:text-[var(--p-tinta)]"
                               visible={verClave} onVisible={setVerClave} />
            </CampoPuerta>
            {/* EL SEGUNDO CAMPO. Con el ojo abierto los dos se leen; con
                el ojo cerrado, coincidir es la única prueba de que lo
                escrito es lo que se cree. */}
            <CampoPuerta id="password2" etiqueta="Confirma tu contraseña"
                         error={errorDe('password2')}
                         ayuda={f.password2 && f.password2 !== f.password
                           ? 'Todavía no coincide con la de arriba.'
                           : 'Escríbela otra vez, igual.'}>
              <CampoContrasena id="password2"
                               autoComplete="new-password"
                               value={f.password2} onChange={set('password2')}
                               placeholder="••••••••••••" className={`${CLASE_CAMPO} pr-24`}
                               claseBoton="text-[var(--p-tinta-2)] hover:text-[var(--p-tinta)]"
                               visible={verClave} onVisible={setVerClave} />
            </CampoPuerta>
            {/* LO FISCAL, AL FINAL Y PLEGADO. No hace falta para tener una
                empresa funcionando y pedirlo de entrada frena el alta. */}
            <div>
              <Plegado resumen="¿Tienes el RFC a la mano? (opcional)">
                <CampoPuerta id="rfc" etiqueta="RFC" opcional error={errorDe('rfc')}
                             ayuda="Se puede poner después, desde Configuración.">
                  <input id="rfc" value={f.rfc} onChange={set('rfc')}
                         placeholder="TWS210415AB1"
                         className={`${CLASE_CAMPO} uppercase`} />
                </CampoPuerta>
              </Plegado>
            </div>
          </>
        )}

        <AvisoError>{error}</AvisoError>

        <div>
          <BotonPuerta apagado={enviando || Boolean(motivo) || (intentado[paso] && faltaEnEstePaso.length > 0)}
                       disabled={enviando || Boolean(motivo)}>
            {enviando && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
            {enviando ? 'Creando tu empresa…' : ultimo ? 'Crear empresa y entrar' : 'Continuar'}
            {!enviando && !ultimo && <ArrowRight className="h-4 w-4" aria-hidden="true" />}
          </BotonPuerta>
          {/* POR QUÉ ESTÁ APAGADO, VISIBLE Y EN VIVO (criterio 8). */}
          {motivo && !enviando && (
            <p data-micro className="mt-2 text-[0.8125rem] leading-relaxed text-[var(--p-tinta-2)]" role="status">
              {motivo}
            </p>
          )}
          {!motivo && intentado[paso] && faltaEnEstePaso.length > 0 && (
            <p data-micro className="mt-2 text-[0.8125rem] text-[var(--p-brasa)]" role="status">
              Revisa {faltaEnEstePaso.length === 1 ? 'el campo marcado' : 'los campos marcados'} para seguir.
            </p>
          )}
        </div>
      </form>

      {/* En teléfono no hay panel izquierdo, así que la garantía se dice
          aquí. SIN NOMBRAR LA TECNOLOGÍA: a quien lleva una bodega
          «PostgreSQL RLS» no le dice nada, y a quien quiera atacarnos le
          dice de más. */}
      <p data-micro className="mt-10 flex items-start gap-2 text-[0.8125rem] leading-relaxed text-[var(--p-tinta-3)]">
        <ShieldCheck className="h-4 w-4 shrink-0 mt-0.5" aria-hidden="true" />
        Tu inventario, aislado del de cualquier otro cliente.
      </p>
    </MarcoPuerta>
  );
}

/* EL OJO. Icono CON palabra —«Ver» / «Ocultar»— porque un icono solo no
   se explica (criterio 2), y del alto del campo entero para que se
   acierte con el pulgar. `tabIndex={-1}`: al tabular se va de contraseña
   a confirmación sin pasar por el ojo, que es lo que espera quien teclea. */
/* EL OJO DE LA CLAVE se mudó a `ui/CampoContrasena` (19 sep 2026): el
   mismo aspecto, pero alcanzable con teclado (aquí llevaba tabIndex -1),
   sin copiar la contraseña visible, y compartido por todas las pantallas
   que piden una contraseña. Aquí queda el aviso para quien lo busque. */

/* EL AVANCE. Tres tramos y no una rueda de porcentaje: se ve de un
   vistazo cuántos quedan, que es la pregunta real de quien está a mitad
   de un alta. */
function Avance({ paso }) {
  return (
    <div>
      <p data-micro className="text-[0.8125rem] font-semibold tracking-wide text-[var(--p-tinta-3)]">
        Paso {paso + 1} de {PASOS.length}
      </p>
      <div className="mt-2 flex gap-1.5" role="progressbar" aria-valuemin={1}
           aria-valuemax={PASOS.length} aria-valuenow={paso + 1}
           aria-label={`Paso ${paso + 1} de ${PASOS.length}: ${PASOS[paso].titulo}`}>
        {PASOS.map((p, i) => (
          <span key={p.titulo}
                className={`h-1 flex-1 rounded-full transition-colors duration-200 ${
                  i <= paso ? 'bg-[var(--p-marca-solido)]' : 'bg-[var(--p-linea)]'}`} />
        ))}
      </div>
    </div>
  );
}

function PanelDeAlta() {
  return (
    <PanelVisual
      fondo="linear-gradient(152deg, #0e5397 0%, #08355f 32%, #061e36 60%, #040c15 100%)"
      /* EL MISMO AZUL QUE LA PUERTA DE ENTRAR, con el rescoldo arriba y
         no abajo: se reconoce que es la misma casa y a la vez que es otro
         momento. */
      halo={
        'radial-gradient(44rem 30rem at 92% 4%, rgba(219,146,60,0.28), transparent 62%),' +
        'radial-gradient(40rem 30rem at 2% 104%, rgba(77,163,240,0.22), transparent 66%)'
      }
      textura={<TexturaPuntos id="alta" />}
      marca={
        <span className="text-2xl font-bold tracking-tight">
          TEGO<span className="text-[#7cbdf7]">Cloud</span>
        </span>
      }
      frase="Tu bodega, ordenada esta misma tarde."
      pie={
        <div className="max-w-[34ch] border-l-2 border-white/25 pl-4">
          <p className="font-semibold text-[var(--p-sobre-noche)]">
            Tu inventario, aislado del de cualquier otro cliente
          </p>
          <p className="mt-1 text-[0.8125rem] leading-relaxed text-[var(--p-sobre-noche-2)]">
            Y te lo puedes llevar entero cuando quieras, en un archivo, sin
            pedir permiso.
          </p>
        </div>
      }
    />
  );
}
